import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppDispatch } from '../hooks/redux';
import { logout } from '../store/slices/authSlice';
import { 
  HomeIcon, 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon,
  DocumentTextIcon,
  CalendarIcon,
  CreditCardIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  PuzzlePieceIcon,
  MicrophoneIcon,
  PhoneIcon,
  HashtagIcon,
  UsersIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Layout: React.FC = () => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Assistants', href: '/assistants', icon: UserGroupIcon },
    { name: 'Call Stats', href: '/call-stats', icon: PhoneIcon },
    { name: 'Phone Numbers', href: '/phone-numbers', icon: HashtagIcon },
    { name: 'Conversations', href: '/conversations', icon: ChatBubbleLeftRightIcon },
    { name: 'Flow Builder', href: '/flows', icon: PuzzlePieceIcon },
    { name: 'Knowledge Base', href: '/knowledge', icon: DocumentTextIcon },
    { name: 'Widgets', href: '/widgets', icon: RectangleStackIcon },
    { name: 'Tags', href: '/tags', icon: DocumentTextIcon },
    { name: 'Vocals Per Minute', href: '/vocals-per-minute', icon: MicrophoneIcon },
    { name: 'Summary Assistant', href: '/summary-assistant', icon: UsersIcon },
    { name: 'Calendar', href: '/calendar', icon: CalendarIcon },
    { name: 'Billing', href: '/billing', icon: CreditCardIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    { name: 'Settings', href: '/settings', icon: CogIcon },
  ];

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error, '- clearing localStorage anyway');
    } finally {
      // Always clear tokens and redirect, even if API call fails
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex">
      {/* Sidebar */}
      <div className="w-64 relative z-10" style={{
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b border-gray-800">
            <div className="flex items-center">
              <div className={`w-8 h-8 bg-gradient-to-r ${colors.primary} rounded-lg flex items-center justify-center`}>
                <span className="text-white font-bold text-sm">L</span>
              </div>
              <span className={`ml-2 text-xl font-semibold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
                Looplet AI
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all ${
                    isActive
                      ? `bg-gradient-to-r ${colors.primary} text-white shadow-lg`
                      : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="border-t border-gray-800 p-4">
            <div className="flex items-center mb-3">
              <div className={`w-8 h-8 bg-gradient-to-r ${colors.primary} rounded-full flex items-center justify-center`}>
                <span className="text-white font-medium text-sm">
                  {user?.firstName?.[0] || 'J'}{user?.lastName?.[0] || 'D'}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">
                  {user?.firstName || 'John'} {user?.lastName || 'Doe'}
                </p>
                <p className="text-xs text-gray-400">{user?.email || 'john.doe@example.com'}</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;